import { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { API } from '@/App';
import { toast } from 'sonner';
import { ArrowRight, SkipForward, Check, X, Eye, EyeOff } from 'lucide-react';
import TreeLogo from '@/components/TreeLogo';

const SignupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = basic info, 2 = demographics
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Honeypot field for bot protection (bots will fill this, humans won't see it)
  const [honeypot, setHoneypot] = useState('');
  
  // Password validation
  const passwordRequirements = useMemo(() => ({
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    minLength: password.length >= 8,
  }), [password]);
  
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  
  // Demographics (optional)
  const [ageRange, setAgeRange] = useState('');
  const [gender, setGender] = useState('');
  const [faithBackground, setFaithBackground] = useState('');
  const [howFoundUs, setHowFoundUs] = useState('');

  const handleSignup = async (includeDemographics = false) => {
    setLoading(true);

    try {
      const payload = { name, email, password };
      
      if (includeDemographics) {
        if (ageRange) payload.age_range = ageRange;
        if (gender) payload.gender = gender;
        if (faithBackground) payload.faith_background = faithBackground;
        if (howFoundUs) payload.how_found_us = howFoundUs;
      }

      const response = await axios.post(
        `${API}/auth/signup`,
        payload,
        { withCredentials: true }
      );
      toast.success('Account created! Welcome to Rooted.');
      navigate('/subscribe', { state: { user: response.data.user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBasicSubmit = (e) => {
    e.preventDefault();
    
    // Bot detection - if honeypot is filled, it's a bot
    if (honeypot) {
      toast.error('Signup failed');
      return;
    }
    
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    setStep(2);
  };

  const handleSkipDemographics = () => {
    handleSignup(false);
  };

  const handleSubmitWithDemographics = (e) => {
    e.preventDefault();
    handleSignup(true);
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Softer gradient background */}
      <div 
        className="absolute inset-0"
        style={{ 
          background: 'linear-gradient(145deg, hsl(250 12% 90%) 0%, hsl(45 25% 95%) 40%, hsl(15 30% 94%) 70%, hsl(250 10% 92%) 100%)'
        }}
      ></div>
      
      {/* Decorative circles - muted */}
      <div className="absolute top-1/4 left-1/6 w-48 h-48 rounded-full opacity-15" style={{ backgroundColor: 'hsl(250 12% 78%)' }}></div>
      <div className="absolute bottom-1/4 right-1/6 w-40 h-40 rounded-full opacity-12" style={{ backgroundColor: 'hsl(15 30% 82%)' }}></div>
      <div className="absolute top-1/2 right-1/4 w-24 h-24 rounded-full opacity-10" style={{ backgroundColor: 'hsl(45 40% 80%)' }}></div>
      
      <Card className="relative z-10 w-full max-w-md border-0 shadow-xl rounded-3xl" style={{ backgroundColor: 'white' }}>
        {step === 1 ? (
          <>
            <CardHeader className="text-center pt-10">
              {/* Tree Logo */}
              <div className="mb-4 mx-auto w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(15 35% 88%)' }}>
                <TreeLogo size={40} className="text-slate-600" />
              </div>
              <CardTitle className="text-3xl" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 40%)' }}>
                Begin Your Journey
              </CardTitle>
              <CardDescription style={{ color: 'hsl(220 12% 50%)' }}>Create an account to access Rooted</CardDescription>
            </CardHeader>
            
            <CardContent className="px-8">
              <form onSubmit={handleBasicSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" style={{ color: 'hsl(250 12% 40%)' }}>Name</Label>
                  <Input
                    id="name"
                    data-testid="signup-name-input"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="mt-1 rounded-xl border-2"
                    style={{ borderColor: 'hsl(250 10% 85%)' }}
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" style={{ color: 'hsl(250 12% 40%)' }}>Email</Label>
                  <Input
                    id="email"
                    data-testid="signup-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1 rounded-xl border-2"
                    style={{ borderColor: 'hsl(250 10% 85%)' }}
                  />
                </div>
                
                <div>
                  <Label htmlFor="password" style={{ color: 'hsl(250 12% 40%)' }}>Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      data-testid="signup-password-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="rounded-xl border-2 pr-10"
                      style={{ borderColor: 'hsl(250 10% 85%)' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  
                  {/* Honeypot field - hidden from humans, bots will fill it */}
                  <div className="absolute -left-[9999px]" aria-hidden="true">
                    <Input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>
                  
                  {/* Password Requirements */}
                  {password.length > 0 && (
                    <div className="mt-3 p-3 rounded-xl" style={{ backgroundColor: 'hsl(250 10% 96%)' }}>
                      <p className="text-xs font-medium mb-2" style={{ color: 'hsl(250 12% 40%)' }}>
                        Password requirements:
                      </p>
                      <ul className="space-y-1">
                        <li className="flex items-center gap-2 text-xs">
                          {passwordRequirements.minLength ? (
                            <Check className="w-3.5 h-3.5 text-green-600" strokeWidth={2.5} />
                          ) : (
                            <X className="w-3.5 h-3.5 text-red-500" strokeWidth={2.5} />
                          )}
                          <span style={{ color: passwordRequirements.minLength ? 'hsl(140 50% 35%)' : 'hsl(0 50% 45%)' }}>
                            At least 8 characters
                          </span>
                        </li>
                        <li className="flex items-center gap-2 text-xs">
                          {passwordRequirements.hasUppercase ? (
                            <Check className="w-3.5 h-3.5 text-green-600" strokeWidth={2.5} />
                          ) : (
                            <X className="w-3.5 h-3.5 text-red-500" strokeWidth={2.5} />
                          )}
                          <span style={{ color: passwordRequirements.hasUppercase ? 'hsl(140 50% 35%)' : 'hsl(0 50% 45%)' }}>
                            At least 1 uppercase letter (A-Z)
                          </span>
                        </li>
                        <li className="flex items-center gap-2 text-xs">
                          {passwordRequirements.hasLowercase ? (
                            <Check className="w-3.5 h-3.5 text-green-600" strokeWidth={2.5} />
                          ) : (
                            <X className="w-3.5 h-3.5 text-red-500" strokeWidth={2.5} />
                          )}
                          <span style={{ color: passwordRequirements.hasLowercase ? 'hsl(140 50% 35%)' : 'hsl(0 50% 45%)' }}>
                            At least 1 lowercase letter (a-z)
                          </span>
                        </li>
                        <li className="flex items-center gap-2 text-xs">
                          {passwordRequirements.hasNumber ? (
                            <Check className="w-3.5 h-3.5 text-green-600" strokeWidth={2.5} />
                          ) : (
                            <X className="w-3.5 h-3.5 text-red-500" strokeWidth={2.5} />
                          )}
                          <span style={{ color: passwordRequirements.hasNumber ? 'hsl(140 50% 35%)' : 'hsl(0 50% 45%)' }}>
                            At least 1 number (0-9)
                          </span>
                        </li>
                        <li className="flex items-center gap-2 text-xs">
                          {passwordRequirements.hasSpecial ? (
                            <Check className="w-3.5 h-3.5 text-green-600" strokeWidth={2.5} />
                          ) : (
                            <X className="w-3.5 h-3.5 text-red-500" strokeWidth={2.5} />
                          )}
                          <span style={{ color: passwordRequirements.hasSpecial ? 'hsl(140 50% 35%)' : 'hsl(0 50% 45%)' }}>
                            At least 1 special character (!@#$%^&*...)
                          </span>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
                
                <Button 
                  data-testid="signup-submit-btn"
                  type="submit" 
                  className="w-full rounded-full py-6 shadow-md hover:shadow-lg transition-shadow"
                  style={{ backgroundColor: 'hsl(250 15% 60%)', color: 'white' }}
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              </form>
              
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" style={{ borderColor: 'hsl(250 10% 90%)' }}></span>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="px-2" style={{ backgroundColor: 'white', color: 'hsl(220 12% 55%)' }}>Or continue with</span>
                </div>
              </div>
              
              <Button
                data-testid="google-signup-btn"
                type="button"
                variant="outline"
                className="w-full rounded-full py-6 border-2"
                style={{ borderColor: 'hsl(250 10% 85%)', color: 'hsl(250 15% 40%)' }}
                onClick={handleGoogleSignup}
              >
                <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>
            </CardContent>
            
            <CardFooter className="flex-col space-y-2 pb-10">
              <p className="text-sm text-center" style={{ color: 'hsl(220 12% 50%)' }}>
                Already have an account?{' '}
                <Link to="/login" className="font-medium hover:underline" style={{ color: 'hsl(250 15% 50%)' }} data-testid="login-link">
                  Sign in
                </Link>
              </p>
              <Link to="/" className="text-sm hover:underline" style={{ color: 'hsl(220 12% 55%)' }} data-testid="home-link">
                ← Back to home
              </Link>
            </CardFooter>
          </>
        ) : (
          <>
            <CardHeader className="text-center">
              <CardTitle className="text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                Help Us Serve You Better
              </CardTitle>
              <CardDescription>
                This optional information helps us improve your experience
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmitWithDemographics} className="space-y-4">
                <div>
                  <Label htmlFor="age">Age Range</Label>
                  <Select value={ageRange} onValueChange={setAgeRange}>
                    <SelectTrigger className="mt-1" data-testid="age-range-select">
                      <SelectValue placeholder="Select your age range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="18-24">18-24</SelectItem>
                      <SelectItem value="25-34">25-34</SelectItem>
                      <SelectItem value="35-44">35-44</SelectItem>
                      <SelectItem value="45-54">45-54</SelectItem>
                      <SelectItem value="55-64">55-64</SelectItem>
                      <SelectItem value="65+">65+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="gender">Gender</Label>
                  <Select value={gender} onValueChange={setGender}>
                    <SelectTrigger className="mt-1" data-testid="gender-select">
                      <SelectValue placeholder="Select your gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="non-binary">Non-binary</SelectItem>
                      <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="faith">Faith Background</Label>
                  <Select value={faithBackground} onValueChange={setFaithBackground}>
                    <SelectTrigger className="mt-1" data-testid="faith-select">
                      <SelectValue placeholder="Select your faith background" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="catholic">Catholic</SelectItem>
                      <SelectItem value="protestant">Protestant</SelectItem>
                      <SelectItem value="orthodox">Orthodox</SelectItem>
                      <SelectItem value="non-denominational">Non-denominational</SelectItem>
                      <SelectItem value="exploring">Exploring faith</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="source">How did you find us?</Label>
                  <Select value={howFoundUs} onValueChange={setHowFoundUs}>
                    <SelectTrigger className="mt-1" data-testid="source-select">
                      <SelectValue placeholder="Select how you found us" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="search">Search engine</SelectItem>
                      <SelectItem value="social_media">Social media</SelectItem>
                      <SelectItem value="friend">Friend or family</SelectItem>
                      <SelectItem value="church">Church or community</SelectItem>
                      <SelectItem value="podcast">Podcast or blog</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button 
                    data-testid="skip-demographics-btn"
                    type="button" 
                    variant="outline"
                    className="flex-1 rounded-full" 
                    onClick={handleSkipDemographics}
                    disabled={loading}
                  >
                    <SkipForward className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Skip
                  </Button>
                  <Button 
                    data-testid="complete-signup-btn"
                    type="submit" 
                    className="flex-1 rounded-full" 
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Complete Signup'}
                  </Button>
                </div>
              </form>
              
              <p className="text-xs text-muted-foreground text-center mt-4">
                Your information is kept private and only used to improve our content and features.
              </p>
            </CardContent>
            
            <CardFooter className="justify-center">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setStep(1)}
                data-testid="back-to-step1-btn"
              >
                ← Back
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
};

export default SignupPage;
